import React from 'react';
import { cn } from '@/lib/utils';
import AnimatedLogoLoader from '@/components/ui/AnimatedLogoLoader';

interface UnifiedLoaderProps {
  mode?: 'fullscreen' | 'page' | 'inline';
  text?: string;
  showProgress?: boolean;
  className?: string;
}

/**
 * UnifiedLoader - Single branded loading component for all loading states
 * 
 * Modes:
 * - fullscreen: Covers entire viewport (boot, auth, protected routes)
 * - page: Partial height (route transitions within layout)
 * - inline: Minimal (button spinners, small areas)
 */
const UnifiedLoader: React.FC<UnifiedLoaderProps> = ({
  mode = 'fullscreen',
  text,
  showProgress = false,
  className,
}) => {
  // Size mapping based on mode
  const sizeMap = {
    fullscreen: 'fullscreen' as const,
    page: 'lg' as const,
    inline: 'md' as const,
  };

  if (mode === 'inline') {
    return (
      <div className={cn('flex items-center justify-center p-4', className)}>
        <AnimatedLogoLoader 
          size={sizeMap[mode]} 
          text={text}
          showProgress={showProgress}
        />
      </div>
    );
  }

  // Both page and fullscreen use white background with subtle blue glow
  const isFullscreen = mode === 'fullscreen';
  
  return (
    <div className={cn(
      'flex items-center justify-center relative fixed inset-0 z-50 bg-background',
      className
    )}>
      {/* Subtle radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.08)_0%,transparent_60%)] pointer-events-none" />

      {/* Content */}
      <div className="relative z-10">
        <AnimatedLogoLoader 
          size={sizeMap[mode]} 
          text={text}
          showProgress={showProgress}
        />
      </div>
    </div>
  );
};

export default UnifiedLoader;
